import { AfterViewInit, Directive, Host, Input, OnChanges, Optional, Renderer2 } from '@angular/core';
import { DziViewerComponent } from '../../dzi-viewer.component';
import OpenSeadragon from 'openseadragon';

enum TRulerOption {
  NONE = 'none',
  LINE = 'line',
  CROSS = 'cross',
  CIRCLE = 'circle',
}

@Directive({
  selector: '[tsDziRuler]',
  standalone: false,
})
export class DziRulerDirective implements OnChanges, AfterViewInit {
  @Input() rulerIcon: TRulerOption;
  @Input() rulerSize: number;
  @Input() hspan: number;

  private _id = 'ruler';

  constructor(@Host() @Optional() private dziViewer: DziViewerComponent, private renderer: Renderer2) {}

  ngOnChanges(): void {
    if (!this.rulerIcon || !this.rulerSize || !this.hspan) return;

    this.updateRuler();
  }

  ngAfterViewInit(): void {
    if (!this.dziViewer) return;

    this.dziViewer['viewer'].addHandler('zoom', () => {
      this.updateRulerSize();
    });
  }

  private updateRuler(): void {
    if (!this.dziViewer['viewer']) return;

    const viewer = this.dziViewer['viewer'];

    const rulerSizePix = this.calculateRulerSize();

    const oldRuler = document.getElementById(this._id);

    if (oldRuler) {
      viewer.removeOverlay(oldRuler);
    }

    if (this.rulerIcon === TRulerOption.NONE) return;

    const newRuler = this.renderer.createElement('div');
    this.renderer.setAttribute(newRuler, 'id', this._id);

    this.renderer.setStyle(newRuler, 'width', `${rulerSizePix}px`);
    this.renderer.setStyle(newRuler, 'height', `${rulerSizePix}px`);
    this.renderer.setStyle(newRuler, 'cursor', 'grab');

    // SVG
    const svg = this.renderer.createElement('svg', 'svg');
    this.renderer.setAttribute(svg, 'width', '100%');
    this.renderer.setAttribute(svg, 'height', '100%');

    switch (this.rulerIcon) {
      case 'circle': {
        const circle = this.renderer.createElement('circle', 'svg');
        this.renderer.setAttribute(circle, 'cx', '50%');
        this.renderer.setAttribute(circle, 'cy', '50%');
        this.renderer.setAttribute(circle, 'r', '47.5%');
        this.renderer.setAttribute(circle, 'fill', 'transparent');
        this.renderer.setAttribute(circle, 'stroke', 'rgb(33,33,33)');
        this.renderer.setAttribute(circle, 'stroke-width', '5%');
        this.renderer.appendChild(svg, circle);
        break;
      }

      case 'line': {
        const lineMain = this.createLine('0%', '50%', '100%', '50%');
        const tickLeft = this.createLine('0%', '60%', '0%', '40%');
        const tickRight = this.createLine('100%', '60%', '100%', '40%');
        [lineMain, tickLeft, tickRight].forEach((line) => this.renderer.appendChild(svg, line));
        break;
      }

      case 'cross': {
        const hMain = this.createLine('0%', '50%', '100%', '50%');
        const hTicks = [this.createLine('0%', '60%', '0%', '40%'), this.createLine('100%', '60%', '100%', '40%')];
        const vMain = this.createLine('50%', '0%', '50%', '100%');
        const vTicks = [this.createLine('40%', '0%', '60%', '0%'), this.createLine('40%', '100%', '60%', '100%')];
        [hMain, ...hTicks, vMain, ...vTicks].forEach((line) => this.renderer.appendChild(svg, line));
        break;
      }
    }

    this.renderer.appendChild(newRuler, svg);

    // TEXT
    const text = this.renderer.createElement('div');
    this.renderer.setAttribute(text, 'id', 'ruler-text');

    const fontSize = rulerSizePix * 0.2;

    this.renderer.setStyle(text, 'fontSize', `${fontSize}px`);
    this.renderer.setStyle(text, 'height', `${fontSize}px`);
    this.renderer.setStyle(text, 'textShadow', '0 0 1px white, 0 0 2px white');
    this.renderer.setStyle(text, 'fontWeight', 'bold');
    this.renderer.setStyle(text, 'width', '100%');
    this.renderer.setStyle(text, 'textAlign', 'center');
    this.renderer.setStyle(text, 'position', 'absolute');
    this.renderer.setStyle(text, 'top', `-${fontSize + 15}px`);

    const textContent = this.renderer.createText(`${this.rulerSize} µm`);
    this.renderer.appendChild(text, textContent);
    this.renderer.appendChild(newRuler, text);

    // OVERLAY
    const center = viewer.viewport.getCenter();
    const sizeInImageCoords = viewer.viewport.deltaPointsFromPixelsNoRotate(
      new OpenSeadragon.Point(rulerSizePix, rulerSizePix)
    );
    const rect = new OpenSeadragon.Rect(
      center.x - sizeInImageCoords.x / 2,
      center.y - sizeInImageCoords.y / 2,
      sizeInImageCoords.x,
      sizeInImageCoords.y
    );

    viewer.addOverlay({
      element: newRuler,
      location: rect,
    });

    this.addRulerHandlers(viewer, newRuler);
  }

  private updateRulerSize(): void {
    const rulerSizePix = this.calculateRulerSize();

    const rulerContainer = document.getElementById(this._id);
    if (!rulerContainer) return;

    this.renderer.setStyle(rulerContainer, 'width', `${rulerSizePix}px`);
    this.renderer.setStyle(rulerContainer, 'height', `${rulerSizePix}px`);

    const textElement = document.getElementById('ruler-text');
    if (!textElement) return;

    const fontSize = rulerSizePix * 0.2;

    this.renderer.setStyle(textElement, 'fontSize', `${fontSize}px`);
    this.renderer.setStyle(textElement, 'height', `${fontSize}px`);
    this.renderer.setStyle(textElement, 'top', `-${fontSize + 15}px`);
  }

  private calculateRulerSize(): number {
    if (!this.dziViewer['viewer']) return;

    const zoom = this.dziViewer['viewer'].viewport.getZoom();
    const containerWidth = this.dziViewer['viewer'].viewport.getContainerSize().x;

    const rulerMm = this.rulerSize / 1000;
    const sizeRatio = rulerMm / this.hspan;

    return sizeRatio * containerWidth * zoom;
  }

  private createLine(x1: string, y1: string, x2: string, y2: string): SVGLineElement {
    const line = this.renderer.createElement('line', 'svg');
    this.renderer.setAttribute(line, 'x1', x1);
    this.renderer.setAttribute(line, 'y1', y1);
    this.renderer.setAttribute(line, 'x2', x2);
    this.renderer.setAttribute(line, 'y2', y2);
    this.renderer.setAttribute(line, 'stroke', '#212121');
    this.renderer.setAttribute(line, 'stroke-width', '5%');
    return line;
  }

  private addRulerHandlers(viewer: OpenSeadragon.Viewer, container: HTMLDivElement) {
    const movementTracker = new OpenSeadragon.MouseTracker({
      element: container,

      pressHandler: () => {
        this.renderer.setStyle(container, 'cursor', 'grabbing');
      },

      dragHandler: (event: any) => {
        const { delta } = event;

        const element = viewer.getOverlayById(container.id);
        const bounds = element.getBounds(viewer.viewport);
        const offset = viewer.viewport.deltaPointsFromPixelsNoRotate(delta);

        const newBounds = bounds.translate(offset);

        viewer.updateOverlay(container.id, newBounds);
      },

      releaseHandler: () => {
        this.renderer.setStyle(container, 'cursor', 'grab');
      },
    });

    movementTracker.setTracking(true);
  }
}
